import { Card, Group, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * A list row, as a card.
 *
 * The whole of Option A rests on this: one card per thing, read top to bottom,
 * every card the same weight. A queue is scanned, not studied, so the primary
 * line is what identifies the record and everything else is subordinate to it.
 *
 * `to` / `onClick` makes the whole card the target rather than a chevron. A
 * 40px chevron is a miss on a phone; a whole card is not.
 *
 * A tappable card is rendered as a real <a> or <button>, never a div with a
 * handler on it. The first version of this was the latter, which looks
 * identical and is not the same thing: no focus ring, no Enter or Space, no
 * role announced, and no middle-click or open-in-new-tab on the desktop
 * queues. Prefer `to` where the card navigates, which is nearly always.
 */
export function RecordCard({
  primary,
  secondary,
  badges,
  meta,
  action,
  accent,
  leading,
  to,
  onClick,
  ariaLabel,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  /** StateBadges, usually. Wraps rather than truncating. */
  badges?: ReactNode;
  /** The quiet footer line: serial numbers, timestamps, counts. */
  meta?: ReactNode;
  /** A single right-hand control. Keep it to one. */
  action?: ReactNode;
  /** A 3px left border for a row needing attention. A theme colour name. */
  accent?: string;
  /**
   * An icon left of the text. For a card standing for a THING - a module, a
   * stream - never for a queue row, where an icon repeated down a list is
   * noise that pushes the text every row is scanned for out of alignment.
   */
  leading?: ReactNode;
  /** Where the card goes. Rendered as a link, so the browser's own gestures work. */
  to?: string;
  /** For a card that acts rather than navigates. Rendered as a button. */
  onClick?: () => void;
  /** The accessible name, where the primary line alone would not read well. */
  ariaLabel?: string;
}) {
  const interactive = Boolean(to || onClick);
  const style = {
    cursor: interactive ? "pointer" : undefined,
    // A <button> brings its own font, alignment and full-width default.
    ...(interactive
      ? { textAlign: "left" as const, width: "100%", font: "inherit", color: "inherit" }
      : {}),
    ...(accent ? { borderLeft: `3px solid var(--mantine-color-${accent})` } : {}),
  };

  const body = (
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        {leading && (
          <div
            aria-hidden
            style={{ flexShrink: 0, display: "flex", color: "var(--mantine-color-indigo-7)" }}
          >
            {leading}
          </div>
        )}
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          <Text fz="lg" fw={600} lh={1.35} c="gray.9">
            {primary}
          </Text>
          {/* component="div", not the default <p>.
              
              `secondary` and `meta` are ReactNode, and screens legitimately
              pass a Group of chips or a row of Texts - Products' phone card
              does. A <div> or a <p> inside a <p> is invalid HTML, which React
              reports as a hydration error, and it surfaced only when the
              route sweep first opened Products at 320px: the browser had been
              silently re-parenting the markup on every render. `primary` is a
              string and keeps its paragraph. */}
          {secondary && (
            <Text component="div" fz="md" c="gray.7" lh={1.45}>
              {secondary}
            </Text>
          )}
          {badges && (
            <Group gap="xs" mt={2} wrap="wrap">
              {badges}
            </Group>
          )}
          {meta && (
            <Text component="div" fz="sm" c="gray.6" lh={1.4} mt={2}>
              {meta}
            </Text>
          )}
        </Stack>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </Group>
  );

  // Three explicit roots rather than a spread of props, because spreading a
  // union of {component, to} | {component, type, onClick} onto Card gives up
  // the polymorphic typing and TypeScript rejects it outright.
  if (to) {
    return (
      <Card component={Link} to={to} withBorder radius="md" padding="md"
            aria-label={ariaLabel} style={style}>
        {body}
      </Card>
    );
  }
  if (onClick) {
    return (
      <Card component="button" type="button" onClick={onClick} withBorder radius="md"
            padding="md" aria-label={ariaLabel} style={style}>
        {body}
      </Card>
    );
  }
  return (
    <Card withBorder radius="md" padding="md" aria-label={ariaLabel} style={style}>
      {body}
    </Card>
  );
}
